import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { searchHotels, searchCities } from './services/hotelService';
import { formatPrice, formatCurrency, getCurrency, initializeCurrency, convertCurrency } from './utils/currency';
import './HotelsPage.css';
import './HotelCard.css';

const HotelsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState({
    cityCode: '',
    cityName: '',
    checkInDate: '',
    checkOutDate: '',
    adults: 1,
    roomQuantity: 1,
    currency: 'NGN'
  });
  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [error, setError] = useState(null);
  const [searchExecuted, setSearchExecuted] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchingCities, setSearchingCities] = useState(false);

  // Initialize currency with location detection (like flights)
  useEffect(() => {
    const initCurrency = async () => {
      try {
        console.log('HotelsPage: Initializing currency with fresh detection...');
        const detectedCurrency = await initializeCurrency(true);
        setCurrency(detectedCurrency);
        
        // Set search currency to detected currency (for API request)
        if (detectedCurrency && detectedCurrency.code) {
          setSearchParams(prev => ({ ...prev, currency: detectedCurrency.code }));
        }
        
        console.log('HotelsPage: Currency initialized:', {
          code: detectedCurrency.code,
          symbol: detectedCurrency.symbol,
          name: detectedCurrency.name
        });
      } catch (error) {
        console.error('HotelsPage: Error initializing currency:', error);
        const fallbackCurrency = getCurrency();
        setCurrency(fallbackCurrency);
        if (fallbackCurrency && fallbackCurrency.code) {
          setSearchParams(prev => ({ ...prev, currency: fallbackCurrency.code }));
        }
      }
    };
    
    initCurrency();

    // Listen for currency detection events
    const handleCurrencyDetected = (event) => {
      console.log('HotelsPage: Currency detected:', event.detail);
      if (event.detail.currency) {
        setCurrency(event.detail.currency);
        setSearchParams(prev => ({ ...prev, currency: event.detail.currency.code }));
      }
    };
    
    window.addEventListener('currencyDetected', handleCurrencyDetected);

    return () => {
      window.removeEventListener('currencyDetected', handleCurrencyDetected);
    };
  }, []);

  // Set default dates
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkIn = tomorrow.toISOString().split('T')[0];

    const dayAfter = new Date(tomorrow);
    dayAfter.setDate(dayAfter.getDate() + 1);
    const checkOut = dayAfter.toISOString().split('T')[0];

    setSearchParams(prev => ({
      ...prev,
      checkInDate: prev.checkInDate || checkIn,
      checkOutDate: prev.checkOutDate || checkOut
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cityName') {
      setSearchParams(prev => ({
        ...prev,
        cityName: value,
        cityCode: '' // Clear city code when user types
      }));
      
      // Search for cities when user types
      if (value.length >= 2) {
        searchCitySuggestions(value);
      } else {
        setCitySuggestions([]);
        setShowSuggestions(false);
      }
    } else {
      setSearchParams(prev => ({
        ...prev,
        [name]: name === 'adults' || name === 'roomQuantity' ? parseInt(value) || 1 : value
      }));
    }
  };

  const searchCitySuggestions = async (query) => {
    setSearchingCities(true);
    try {
      const result = await searchCities(query);
      if (result.success) {
        setCitySuggestions(result.data || []);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error('City search error:', err);
    } finally {
      setSearchingCities(false);
    }
  };

  const selectCity = (city) => {
    setSearchParams(prev => ({
      ...prev,
      cityName: city.fullName || city.name,
      cityCode: city.cityCode
    }));
    setShowSuggestions(false);
    setCitySuggestions([]);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate city code is selected
    if (!searchParams.cityCode) {
      setError('Please select a city from the suggestions');
      return;
    }
    
    setLoading(true);
    setError(null);
    setHotels([]);
    setSearchExecuted(false);
    setShowSuggestions(false);

    try {
      const result = await searchHotels(searchParams);
      
      if (result.success) {
        setHotels(result.data);
        setSearchExecuted(true);
      } else {
        setError(result.error || 'Failed to search hotels');
        console.error('Search error:', result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      console.error('Search exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPriceDisplay = (price) => {
    if (!price || !price.total) return 'Price not available';
    
    const userCurrency = currency || getCurrency() || { code: 'NGN', symbol: '₦' };
    const apiCurrency = (price.currency || searchParams.currency || 'NGN').toUpperCase();
    const apiAmount = parseFloat(price.total);
    
    if (isNaN(apiAmount) || apiAmount <= 0) {
      return 'Price not available';
    }
    
    // If API currency matches user currency, show directly
    if (apiCurrency === userCurrency.code) {
      return formatCurrency(apiAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    }
    
    // Convert from API currency to user's currency
    // convertCurrency handles: API currency → NGN → User currency
    try {
      const convertedAmount = convertCurrency(apiAmount, apiCurrency, userCurrency.code);
      
      // If conversion failed or returned 0, show API currency with amount
      if (!convertedAmount || convertedAmount <= 0) {
        console.warn(`Currency conversion failed for ${apiCurrency}, showing original`);
        return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
      }
      
      console.log(`🏨 Hotel price conversion: ${apiAmount} ${apiCurrency} → ${convertedAmount} ${userCurrency.code}`);
      
      // Format the converted amount with user's currency (already converted, so use formatCurrency, not formatPrice)
      return formatCurrency(convertedAmount, { 
        decimals: 0, 
        currency: userCurrency 
      });
    } catch (error) {
      console.error('Price conversion error:', error);
      // Fallback: show in API currency
      return `${apiCurrency} ${apiAmount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!searchParams.checkInDate || !searchParams.checkOutDate) return 0;
    const checkIn = new Date(searchParams.checkInDate);
    const checkOut = new Date(searchParams.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Popular cities with both code and full name
  const popularCities = [
    { code: 'PAR', name: 'Paris, France', fullName: 'Paris, FR' },
    { code: 'NYC', name: 'New York, USA', fullName: 'New York, US' },
    { code: 'LON', name: 'London, UK', fullName: 'London, GB' },
    { code: 'DXB', name: 'Dubai, UAE', fullName: 'Dubai, AE' },
    { code: 'LOS', name: 'Lagos, Nigeria', fullName: 'Lagos, NG' },
    { code: 'ABV', name: 'Abuja, Nigeria', fullName: 'Abuja, NG' },
    { code: 'SYD', name: 'Sydney, Australia', fullName: 'Sydney, AU' },
    { code: 'TYO', name: 'Tokyo, Japan', fullName: 'Tokyo, JP' },
  ];

  const handleQuickSelectCity = (city) => {
    setSearchParams(prev => ({
      ...prev,
      cityName: city.fullName || city.name,
      cityCode: city.code
    }));
    setShowSuggestions(false);
    setCitySuggestions([]);
  };

  return (
    <div className="hotels-page">
      <Header />
      
      <div className="hotels-page-container">
        <div className="hotels-page-header">
          <h1>Find Your Perfect Hotel</h1>
          <p>Search and book hotels worldwide with real-time availability and best prices</p>
        </div>

        {/* Search Form */}
        <div className="hotels-search-form">
          <form onSubmit={handleSearch}>
            <div className="form-row">
              <div className="form-group city-search-group">
                <label>City</label>
                <div className="city-input-wrapper">
                  <input
                    type="text"
                    name="cityName"
                    value={searchParams.cityName}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (citySuggestions.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => {
                      // Delay to allow click on suggestion
                      setTimeout(() => setShowSuggestions(false), 200);
                    }}
                    placeholder="Type city name (e.g., Paris, New York, Lagos)"
                    required
                    autoComplete="off"
                  />
                  {searchingCities && (
                    <span className="city-search-loading">🔍</span>
                  )}
                  {showSuggestions && citySuggestions.length > 0 && (
                    <div className="city-suggestions-dropdown">
                      {citySuggestions.map((city, idx) => (
                        <div
                          key={idx}
                          className="city-suggestion-item"
                          onClick={() => selectCity(city)}
                        >
                          <div className="suggestion-city-name">{city.fullName || city.name}</div>
                          {city.countryName && (
                            <div className="suggestion-country">{city.countryName}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {searchParams.cityCode && (
                  <small>City Code: {searchParams.cityCode}</small>
                )}
              </div>

              <div className="form-group">
                <label>Check-In Date</label>
                <input
                  type="date"
                  name="checkInDate"
                  value={searchParams.checkInDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label>Check-Out Date</label>
                <input
                  type="date"
                  name="checkOutDate"
                  value={searchParams.checkOutDate}
                  onChange={handleInputChange}
                  required
                  min={searchParams.checkInDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Adults</label>
                <input
                  type="number"
                  name="adults"
                  value={searchParams.adults}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label>Rooms</label>
                <input
                  type="number"
                  name="roomQuantity"
                  value={searchParams.roomQuantity}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label>Currency (for API request)</label>
                <select
                  name="currency"
                  value={searchParams.currency}
                  onChange={handleInputChange}
                >
                  <option value="NGN">NGN (Naira)</option>
                  <option value="USD">USD (Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="GBP">GBP (Pound)</option>
                  <option value="AED">AED (Dirham)</option>
                  <option value="ZAR">ZAR (Rand)</option>
                  <option value="GHS">GHS (Cedi)</option>
                  <option value="KES">KES (Shilling)</option>
                </select>
                {currency && (
                  <small>Prices will be displayed in {currency.symbol} {currency.code} based on your location</small>
                )}
              </div>
            </div>

            <div className="popular-cities">
              <label>Quick Select:</label>
              <div className="city-buttons">
                {popularCities.map(city => (
                  <button
                    key={city.code}
                    type="button"
                    className="city-button"
                    onClick={() => handleQuickSelectCity(city)}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            </div>

            <button 
              type="submit" 
              className="search-button"
              disabled={loading}
            >
              {loading ? 'Searching Hotels...' : 'Search Hotels'}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Searching for hotels...</p>
          </div>
        )}

        {/* Results */}
        {searchExecuted && !loading && (
          <div className="results-section">
            <h2>
              {hotels.length > 0 
                ? `Found ${hotels.length} Hotel${hotels.length !== 1 ? 's' : ''} in ${searchParams.cityName || searchParams.cityCode}`
                : 'No Hotels Found'
              }
            </h2>

            {searchParams.checkInDate && searchParams.checkOutDate && (
              <div className="search-summary">
                <span>{formatDate(searchParams.checkInDate)}</span>
                <span>→</span>
                <span>{formatDate(searchParams.checkOutDate)}</span>
                <span>({calculateNights()} night{calculateNights() !== 1 ? 's' : ''})</span>
              </div>
            )}

            {hotels.length > 0 ? (
              <div className="hotel-card-wrapper">
                {hotels.map((hotel, index) => (
                  <div key={hotel.hotelId || index} className="hotel-card-item">

                    {/* Hotel Info */}
                    <div className="hotel-info-box">
                      {/* Hotel Name */}
                      <h3 className="hotel-title">{hotel.name}</h3>

                      {/* Rating */}
                      {hotel.rating && (
                        <div className="hotel-stars">
                          <span>{'⭐'.repeat(Math.floor(hotel.rating))}</span>
                          {hotel.rating % 1 !== 0 && <span>½</span>}
                          <span className="hotel-stars-text">{hotel.rating.toFixed(1)}/5</span>
                        </div>
                      )}

                      {/* Price */}
                      {hotel.price && (
                        <div className="hotel-price-box">
                          <div className="hotel-price-value">{formatPriceDisplay(hotel.price)}</div>
                          <div className="hotel-price-label">per night</div>
                        </div>
                      )}

                      {/* View Details Button */}
                      <button 
                        className="hotel-action-btn"
                        onClick={() => {
                          navigate('/hotel-details', { 
                            state: { 
                              hotel,
                              searchParams 
                            } 
                          });
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-hotels">
                <p>No hotels found for your search criteria. Try a different city code or dates.</p>
                <p><strong>Tip:</strong> Use 3-letter IATA city codes like PAR, NYC, LON, DXB, LOS, ABV</p>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default HotelsPage;
